import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { NotificationType } from 'app/shared/models/notification-type';
import { TermsConditions } from 'app/shared/models/terms-conditions.model';
import { ErrorHandlerService } from 'app/shared/services/error-handler/error-handler.service';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { UserService } from 'app/shared/services/user/user.service';
import { UtilityService } from 'app/shared/services/utility/utility.service';
import { Subscription, catchError, finalize, of, switchMap, tap } from 'rxjs';
import { UserTermsConditionsComponent } from '../user-terms-conditions/user-terms-conditions.component';
import { AppState } from 'app/app.state';
import { GroupPolicyITComponent } from 'app/group-policy-it/group-policy-it.component';

@Component({
  selector: 'upsc-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.scss']
})
export class PopupComponent implements OnInit, OnDestroy, AfterViewInit {

  public content: string;
  public dynamicContent: SafeHtml;
  public title: string;
  public isTermsLoading = false;
  public agreed: boolean;
  public hasButtons: boolean;
  public showCheckbox: boolean;
  public useDynamicContent: boolean;
  public stepNumber: number;
  public termsData: TermsConditions;
  public userCountryCode: string;

  private getTermsSubscription: Subscription;
  private nextStepHasButtons: boolean;

  constructor(private userService: UserService,
    private utilityService: UtilityService,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private errorHandlerService: ErrorHandlerService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef,
    public dialogRef: MatDialogRef<PopupComponent>,
    public readonly appState: AppState,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,) {
    this.stepNumber = data.stepNumber;
    this.termsData = data.termsData;
    this.hasButtons = data.hasButtons;
    this.userCountryCode = this.data.countryCode;
  }

  ngOnInit(): void {
    this.assignData();
  }

  ngAfterViewInit() {
    const checkbox = this.elementRef.nativeElement.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.addEventListener('change', this.nextStep.bind(this));
    }
  }

  public ngOnDestroy() {
    this.utilityService.clearSubscriptions([this.getTermsSubscription]);
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const clickedElement = event.target as Element;
    if (clickedElement.className === 'link') {
      event.preventDefault();
      if (clickedElement.innerHTML === 'Polizza Collettiva')
        this.openGroupPolicyITDialog(event);
      else
        this.openTermsAndConditionsDialog(event);
    }
  }

  assignData() {
    switch (this.stepNumber) {
      case 2:
        this.dynamicContent = this.sanitizer.bypassSecurityTrustHtml(this.termsData.Terms2);
        if (this.userCountryCode === 'IT')
          this.title = 'ParcePro® Select Termini e Condizioni Generali';
        else if (this.userCountryCode === 'DE')
          this.title = 'UPS Capital Technologie-Vereinbarung';
        else if (this.userCountryCode === 'FR')
          this.title = 'ParcePro® Select Conditions Générales';
        else if (this.userCountryCode === 'GB')
          this.title = 'Acceptance of UPS Capital Technology Agreement';
        this.showCheckbox = false;
        this.useDynamicContent = true;
        this.nextStepHasButtons = false;
        break;
      case 3:
        this.dynamicContent = this.sanitizer.bypassSecurityTrustHtml(this.termsData.Terms3);
        if (this.userCountryCode === 'IT')
          this.title = 'Adesione alla Polizza Collecttiva';
        else if (this.userCountryCode === 'FR')
          this.title = 'L’accord sur les technologies d’UPS Capital';
        this.showCheckbox = false;
        this.useDynamicContent = true;
        if (this.userCountryCode === 'FR')
          this.nextStepHasButtons = false;
        else
          this.nextStepHasButtons = true;
        break;
      case 4:
        if (this.userCountryCode === 'IT') {
          this.content = this.termsData.Terms4;
          this.useDynamicContent = false;
          this.title = 'Adesione alla Polizza Collecttiva';
        }
        else if (this.userCountryCode === 'FR') {
          this.title = 'L’accord sur les technologies d’UPS Capital.';
          this.dynamicContent = this.sanitizer.bypassSecurityTrustHtml(this.termsData.Terms4);
          this.useDynamicContent = true;
        }
        this.showCheckbox = false;
        this.nextStepHasButtons = false;
        break;
      case 5:
        this.dynamicContent = this.sanitizer.bypassSecurityTrustHtml(this.termsData.Terms5);
        this.title = 'UPS Capital Technology Agreement';
        this.showCheckbox = false;
        this.useDynamicContent = true;
        this.nextStepHasButtons = true;
        break;
      case 6:
        this.content = this.termsData.Terms6;
        this.title = 'UPS Capital Technology Agreement';
        break;
    }
  }

  nextStep() {
    if ((this.stepNumber === 6 && this.userCountryCode === 'IT') ||
      (this.stepNumber === 2 && this.userCountryCode === 'DE') ||
      (this.stepNumber === 4 && this.userCountryCode === 'FR') ||
      (this.stepNumber === 2 && this.userCountryCode === 'GB')) {
      this.dialogRef.close();
      this.saveTerms();
    } else {
      const dialogConfig: MatDialogConfig = {
        disableClose: true,
        width: '70%',
        data: {
          stepNumber: this.stepNumber + 1,
          termsData: this.termsData,
          hasButtons: this.nextStepHasButtons,
          countryCode: this.userCountryCode
        },
      };

      this.dialogRef.close();
      let newDialogRef: MatDialogRef<PopupComponent>;
      newDialogRef = this.dialog.open(PopupComponent, dialogConfig);

      newDialogRef.afterClosed().subscribe((result) => {
      });
    }
  }

  openTermsAndConditionsDialog(event) {
    event.preventDefault();

    const dialogConfig: MatDialogConfig = {
      disableClose: true,
      width: '70%',
      data: {},
    };

    let dialogRef: MatDialogRef<UserTermsConditionsComponent>;
    dialogRef = this.dialog.open(UserTermsConditionsComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
    });
  }

  openGroupPolicyITDialog(event) {
    event.preventDefault();

    const dialogConfig: MatDialogConfig = {
      disableClose: true,
      width: '70%',
      data: {},
    };

    let dialogRef: MatDialogRef<GroupPolicyITComponent>;
    dialogRef = this.dialog.open(GroupPolicyITComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
    });
  }

  decline() {
    this.dialogRef.close();
    this.authenticationService.logout();
  }

  saveTerms(): void {
    this.userService.saveTerms('')
      .pipe(
        catchError((err) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Error Accepting Terms',
            NotificationType.ERROR);

          return of(null);
        }),
        switchMap(() => {
          return this.userService.getUser()
            .pipe(
              tap((user) => {
                if (user) {
                  this.appState.user$.set(user);
                }
              }),
            );
        },
        ),
      )
      .subscribe(
        (user) => {
          this.router.navigate(['dashboard']).then();
        });
  }

}
